<script lang="ts" setup>
  import { computed, ref } from 'vue';
  import { useToggle } from '@vueuse/core';
  import { useI18n } from '@/common/composables/vue-i18n.composable';
  import useTextRowCount from '@/common/composables/useTextRowCount';
  import type { VNode, VueConstructor } from 'vue';

  const { t } = useI18n();

  type Props = {
    text: string | VNode | VueConstructor,
    labelMore?: string,
    labelLess?: string
    visibleRows?: number
    hideToggle?: boolean
  }

  const props = defineProps<Props>();

  const visibleRows = props.visibleRows || 3;
  const textRef = ref<HTMLElement | null>(null);
  const textRowCount = useTextRowCount(textRef);
  const showToggleButton = computed(() => textRowCount.value > visibleRows && !props.hideToggle);

  const clamped = ref(true);
  const labelLess = props.labelLess || t('Less');
  const labelMore = props.labelMore || t('More');
  const toggleText = computed(() => clamped.value ? labelMore : labelLess);
  const toggleMode = useToggle(clamped);

  defineExpose({
    toggleMode,
    clamped,
    textRef,
    textRowCount
  });
</script>

<template>
  <div class="more v-more">
    <div
      ref="textRef"
      :class="['text', { clamped }]"
    >
      {{ text }}
    </div>
    <div
      v-if="showToggleButton"
      class="link"
      @click="toggleMode()"
    >
      {{ toggleText }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
.more {
  height: 100%;
  overflow: hidden;

  .text {
    line-height: $av-line-height-s;

    &.clamped {
      @include truncate();
    }
  }

  .link {
    color: $av-brand-primary;
    line-height: 32px;
    cursor: pointer;
    display: inline-block;

    &:hover {
      opacity: 0.9;
    }
  }
}
</style>
